/**
 * VoiceControls - Push-to-Talk voice controls for Midgard
 * 
 * ALWAYS VISIBLE while in 3D world - does NOT require sitting or nearby players.
 * 
 * Voice routing priority:
 * 1. Social Zone (seated at Bar/Table/Hearth) -> Zone voice room
 * 2. Direct Talk (proximity conversation) -> Direct voice room  
 * 3. World/Proximity Voice (default) -> Scene proximity voice
 * 
 * Supports:
 * - Push-to-Talk (default): Hold V key or PTT button to transmit
 * - Voice Activation: Automatic transmission when speaking
 * 
 * Spatial voice features:
 * - Distance attenuation (volume decreases with distance)
 * - Line-of-sight occlusion (walls block voice)
 * - Observer-dependent (each listener calculates relevance independently)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Mic, MicOff, Radio, Loader, AlertCircle, Users, Volume2 } from 'lucide-react';
import voiceService, { VoiceState, VoiceRoomType } from '../../services/VoiceService';

// Voice configuration
const VOICE_CONFIG = {
  maxProximityRange: 25,  // Maximum distance in world units for proximity voice
  fullVolumeRange: 5,     // Distance at which voice is at full volume
  attenuationCurve: 'linear', // 'linear' or 'exponential'
  updateFrequency: 200,   // ms between spatial voice updates
};

/**
 * Get the voice context based on current player state
 * Returns context for routing voice to appropriate room/scope
 */
function getVoiceContext(sceneId, seatedZoneId, activeConversation, worldId) {
  // Priority 1: Social Zone (seated at bar/table/hearth)
  if (seatedZoneId) {
    return {
      type: 'zone',
      roomId: seatedZoneId,
      label: getZoneLabel(seatedZoneId),
      priority: 1,
    };
  }
  
  // Priority 2: Direct Talk conversation
  if (activeConversation) {
    return {
      type: 'direct',
      roomId: activeConversation.conversationId,
      label: `Talk: ${activeConversation.targetDisplayName}`,
      priority: 2,
    };
  }
  
  // Priority 3: World/Proximity voice (default when exploring in open world)
  if (worldId) {
    return {
      type: 'proximity',
      roomId: `proximity:${worldId}`,
      label: 'Nearby',
      priority: 3,
    };
  }
  
  // Priority 4: Scene-based proximity (legacy/interior scenes)
  if (sceneId) {
    return {
      type: 'proximity',
      roomId: `proximity:${sceneId}`,
      label: 'Nearby',
      priority: 3,
    };
  }
  
  return null;
}

/**
 * Get human-readable zone label
 */
function getZoneLabel(zoneId) {
  if (zoneId.includes('bar')) return 'Bar';
  if (zoneId.includes('table')) {
    const num = zoneId.match(/table-?(\d+)/i)?.[1] || '';
    return `Table ${num}`;
  }
  if (zoneId.includes('hearth')) return 'Hearth';
  return 'Zone';
}

/**
 * Get voice mode display label
 */
function getVoiceModeLabel(voiceContext, isConnected) {
  if (!isConnected) return 'Connecting...';
  if (!voiceContext) return 'Voice Ready';
  
  switch (voiceContext.type) {
    case 'zone': return voiceContext.label;
    case 'direct': return voiceContext.label;
    case 'proximity': return 'Nearby';
    default: return 'Voice';
  }
}

/**
 * Main Voice Controls Component
 * ALWAYS renders PTT button when in 3D world (sceneId provided)
 */
export default function VoiceControls({
  sceneId,              // Current 3D scene ID (required for proximity voice)
  worldId,              // Current world ID for open-world proximity voice (e.g., 'midgard')
  seatedZoneId,         // Social zone ID if seated
  activeConversation,   // Direct talk conversation if active
  gameSettings = {},
  isChatFocused = false,
  nearbyPlayers = [],   // For proximity voice participant display
  onOpenSettings,
  isMobile = false,     // On touch devices, lift clear of the ecctrl joystick/buttons
}) {
  // Voice state
  const [connectionState, setConnectionState] = useState(VoiceState.DISCONNECTED);
  const [isMuted, setIsMuted] = useState(true);
  const [isPTTActive, setIsPTTActive] = useState(false);
  const [error, setError] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  
  // Refs - must be declared before useCallbacks that use them
  const currentRoomRef = useRef(null);
  const pttKeyRef = useRef('KeyV');
  const voiceInputModeRef = useRef('pushToTalk');
  const isJoiningRef = useRef(false);
  const connectionStateRef = useRef(VoiceState.DISCONNECTED);
  
  // Voice context based on current state (use worldId for proximity in open world)
  const voiceContext = getVoiceContext(sceneId, seatedZoneId, activeConversation, worldId);
  
  // Update refs from settings
  useEffect(() => {
    voiceInputModeRef.current = gameSettings.voiceInputMode || 'pushToTalk';
  }, [gameSettings.voiceInputMode]);
  
  // Keep connectionStateRef in sync
  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);
  
  // Voice service callbacks setup
  useEffect(() => {
    voiceService.onStateChange = (state) => {
      setConnectionState(state);
      connectionStateRef.current = state;
    };
    
    voiceService.onMuteChange = (muted) => {
      setIsMuted(muted);
    };
    
    voiceService.onError = (err) => {
      setError(err);
      console.error('Voice error:', err);
    };
    
    voiceService.onParticipantsChange = (count) => {
      setParticipantCount(count);
    };
    
    return () => {
      voiceService.onStateChange = null;
      voiceService.onMuteChange = null;
      voiceService.onError = null;
      voiceService.onParticipantsChange = null;
    };
  }, []);
  
  // ===== DEFINE useCallbacks BEFORE useEffects that reference them =====
  
  // PTT activation - defined BEFORE the useEffect that uses it
  const activatePTT = useCallback(async () => {
    if (connectionStateRef.current !== VoiceState.CONNECTED) return;
    if (gameSettings.micMuted) return;
    
    setIsPTTActive(true);
    try {
      await voiceService.setMuted(false);
    } catch (err) {
      console.error('PTT activate error:', err);
    }
  }, [gameSettings.micMuted]);
  
  // PTT deactivation - defined BEFORE the useEffect that uses it
  const deactivatePTT = useCallback(async () => {
    setIsPTTActive(false);
    if (voiceInputModeRef.current === 'pushToTalk') {
      try {
        await voiceService.setMuted(true);
      } catch (err) {
        console.error('PTT deactivate error:', err);
      }
    }
  }, []);
  
  // Toggle mute (for voice activation mode)
  const toggleMute = useCallback(async () => {
    if (gameSettings.voiceInputMode === 'pushToTalk') {
      onOpenSettings?.();
    } else {
      await voiceService.setMuted(!isMuted);
    }
  }, [gameSettings.voiceInputMode, isMuted, onOpenSettings]);
  
  // ===== NOW define useEffects that use the callbacks =====
  
  // Auto-join/leave voice room based on context
  useEffect(() => {
    const joinVoiceRoom = async () => {
      if (isJoiningRef.current) return;
      
      if (!voiceContext) {
        // Leave voice if no context
        if (voiceService.connectionState !== VoiceState.DISCONNECTED) {
          isJoiningRef.current = true;
          try {
            await voiceService.leaveRoom();
            currentRoomRef.current = null;
          } finally {
            isJoiningRef.current = false;
          }
        }
        return;
      }
      
      const newRoomId = voiceContext.roomId;
      
      if (currentRoomRef.current === newRoomId) {
        return; // Already in correct room
      }
      
      isJoiningRef.current = true;
      try {
        if (currentRoomRef.current) {
          await voiceService.leaveRoom();
        }
        
        // Determine room type
        let roomType;
        switch (voiceContext.type) {
          case 'zone':
            roomType = VoiceRoomType.SOCIAL_ZONE;
            break;
          case 'direct':
            roomType = VoiceRoomType.DIRECT_TALK;
            break;
          case 'proximity':
          default:
            roomType = VoiceRoomType.PROXIMITY;
            break;
        }
        
        await voiceService.joinRoom(newRoomId, roomType);
        currentRoomRef.current = newRoomId;
        
        // Start muted in PTT mode
        if (voiceInputModeRef.current === 'pushToTalk') {
          await voiceService.setMuted(true);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to join voice room:', err);
        setError('Voice connection failed');
      } finally {
        isJoiningRef.current = false;
      }
    };
    
    joinVoiceRoom();
  }, [voiceContext?.roomId, voiceContext?.type]);
  
  // PTT key handling - references activatePTT and deactivatePTT which are now defined above
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (voiceInputModeRef.current !== 'pushToTalk') return;
      if (isChatFocused) return;
      if (e.repeat) return;
      
      if (e.code === pttKeyRef.current) {
        e.preventDefault();
        activatePTT();
      }
    };
    
    const handleKeyUp = (e) => {
      if (voiceInputModeRef.current !== 'pushToTalk') return;
      if (e.code === pttKeyRef.current) {
        e.preventDefault();
        deactivatePTT();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isChatFocused, activatePTT, deactivatePTT]);
  
  // Voice Activation mode handling
  useEffect(() => {
    if (gameSettings.voiceInputMode === 'voiceActivation') {
      if (connectionState === VoiceState.CONNECTED && !gameSettings.micMuted) {
        voiceService.setMuted(false);
      }
    } else {
      if (connectionState === VoiceState.CONNECTED && !isPTTActive) {
        voiceService.setMuted(true);
      }
    }
  }, [gameSettings.voiceInputMode, connectionState, gameSettings.micMuted, isPTTActive]);
  
  // ===== RENDER =====
  
  // ALWAYS show PTT button when in 3D world (sceneId or worldId exists)
  // Do NOT hide based on voice context - PTT should be visible while exploring
  if (!sceneId && !worldId) {
    return null;
  }
  
  const isConnected = connectionState === VoiceState.CONNECTED;
  const isConnecting = connectionState === VoiceState.CONNECTING || connectionState === VoiceState.RECONNECTING;
  const isPTTMode = gameSettings.voiceInputMode === 'pushToTalk';
  const isTransmitting = isPTTMode ? isPTTActive : (!isMuted && isConnected);
  const modeLabel = getVoiceModeLabel(voiceContext, isConnected);
  
  return (
    <div 
      className="fixed z-50"
      style={{
        // On mobile the ecctrl action buttons own the bottom-right corner
        // (row is 52px tall at 24px from the bottom, with the contextual
        // Interact button stacked above it). Lift the PTT clear of both.
        bottom: isMobile
          ? 'calc(env(safe-area-inset-bottom, 0px) + 152px)'
          : 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
      }}
      data-testid="voice-controls"
    >
      {/* Voice Context Label */}
      <div className="mb-2 text-center">
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          voiceContext?.type === 'zone' 
            ? 'bg-amber-900/70 text-amber-200' 
            : voiceContext?.type === 'direct'
              ? 'bg-blue-900/70 text-blue-200'
              : 'bg-black/50 text-white/60'
        }`}>
          {modeLabel}
        </span>
        {participantCount > 1 && isConnected && (
          <div className="mt-1 flex items-center justify-center gap-1 text-xs text-green-400/80">
            <Users className="w-3 h-3" />
            <span>{participantCount}</span>
          </div>
        )}
      </div>
      
      {/* PTT Button */}
      <button
        onMouseDown={isPTTMode ? activatePTT : undefined}
        onMouseUp={isPTTMode ? deactivatePTT : undefined}
        onMouseLeave={isPTTMode ? deactivatePTT : undefined}
        onTouchStart={isPTTMode ? (e) => { e.preventDefault(); activatePTT(); } : undefined}
        onTouchEnd={isPTTMode ? (e) => { e.preventDefault(); deactivatePTT(); } : undefined}
        onTouchCancel={isPTTMode ? deactivatePTT : undefined}
        onClick={!isPTTMode ? toggleMute : undefined}
        disabled={gameSettings.micMuted}
        className={`
          w-16 h-16 rounded-full flex flex-col items-center justify-center
          transition-all duration-150 shadow-lg select-none
          ${gameSettings.micMuted
            ? 'bg-red-800/80 cursor-not-allowed'
            : isTransmitting 
              ? 'bg-green-500 scale-110 shadow-green-500/50' 
              : isConnecting
                ? 'bg-amber-700/80'
                : isConnected
                  ? 'bg-gray-800/90 hover:bg-gray-700/90 active:bg-gray-600/90'
                  : 'bg-gray-700/80'
          }
          border-2 ${isTransmitting ? 'border-green-300' : 'border-white/20'}
        `}
        title={isPTTMode ? 'Hold to talk (V key)' : (isMuted ? 'Unmute' : 'Mute')}
        data-testid="ptt-button"
      >
        {isConnecting ? (
          <Loader className="w-6 h-6 text-white animate-spin" />
        ) : gameSettings.micMuted ? (
          <MicOff className="w-6 h-6 text-red-300" />
        ) : isTransmitting ? (
          <Radio className="w-6 h-6 text-white animate-pulse" />
        ) : (
          <Mic className="w-6 h-6 text-white/70" />
        )}
        
        {!gameSettings.micMuted && !isConnecting && (
          <span className="text-[10px] text-white/60 mt-0.5">
            {isTransmitting ? 'LIVE' : isPTTMode ? 'PTT' : 'VA'}
          </span>
        )}
      </button>
      
      {/* Error message */}
      {error && (
        <div className="mt-2 text-xs text-red-400 bg-black/70 px-2 py-1 rounded flex items-center gap-1 max-w-[120px]">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{error}</span>
        </div>
      )}
      
      {/* Keyboard hint (desktop only) */}
      {isPTTMode && !('ontouchstart' in window) && !gameSettings.micMuted && (
        <div className="mt-1 text-center text-xs text-white/40">
          Hold V to talk
        </div>
      )}
    </div>
  );
}

/**
 * Export voice configuration for use by spatial audio system
 */
export { VOICE_CONFIG };
